import React from 'react'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons'
import { Button, Card, Col, Collapse, Dropdown, Menu, Modal, Row, Select, Tooltip, Typography } from 'antd'
import PropTypes from 'prop-types'

// eslint-disable-next-line import/order
import styles from './UniversalTemplateModules.module.css'

import UniversalTemplateFormFields from './UniversalTemplateFormFields'
import UniversalTemplateHeroFields from './UniversalTemplateHeroFields'
import UniversalTemplateTextFields from './UniversalTemplateTextFields'

const { Panel } = Collapse
const { Option } = Select
const { Title } = Typography

const ADD_MODULE_ITEMS = [
  { key: 'hero', label: 'Hero module' },
  { key: 'text', label: 'Text module' },
  { key: 'form', label: 'Form module' }
]

const DELETE_EVENT_STUB = { stopPropagation: () => {} }

const UniversalTemplateModules = ({
  allGenres = [],
  genre,
  modules = [],
  onAddModule,
  onDeleteModule,
  onGenreChange,
  onReorderModule,
  onUpdateModule
}) => {
  const genreOptions = (allGenres || []).map(g => ({
    label: typeof g === 'string' ? g : (g.name || g.label || ''),
    value: typeof g === 'string' ? g : (g.name || g.value || '')
  })).filter(o => o.value)

  const hasHero = modules.some(m => m.type === 'hero')
  const hasText = modules.some(m => m.type === 'text')
  const hasForm = modules.some(m => m.type === 'form')
  const addMenuItems = ADD_MODULE_ITEMS.filter(({ key }) => {
    if (key === 'hero' && hasHero) return false
    if (key === 'text' && hasText) return false
    if (key === 'form' && hasForm) return false
    return true
  })

  const isAddModuleDisabled = addMenuItems.length === 0

  const confirmDeleteModule = index => {
    Modal.confirm({
      cancelText: 'Cancel',
      centered: true,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => onDeleteModule(DELETE_EVENT_STUB, index),
      title: 'Delete this module?'
    })
  }

  return (
    <div className={styles.root}>
      <Row className={styles.genreSection} gutter={[16, 16]}>
        <Col span={24}>
          <Title level={3}>Genre</Title>
          <Select
            allowClear
            className={styles.genreSelect}
            onChange={onGenreChange}
            placeholder="Select genre"
            showSearch
            value={genre || undefined}
          >
            {genreOptions.map(o => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row align='middle' className={styles.toolbar} gutter={16}>
        <Col><Title level={3}>Modules</Title></Col>
        <Col>
          {isAddModuleDisabled ? (
            <Tooltip title="All module types have been added">
              <span>
                <Button disabled icon={<PlusOutlined />} type="primary">
                  Add module
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Dropdown
              overlay={(
                <Menu onClick={({ key }) => onAddModule(key)}>
                  {addMenuItems.map(({ key, label }) => (
                    <Menu.Item key={key}>{label}</Menu.Item>
                  ))}
                </Menu>
              )}
              trigger={['click']}
            >
              <Button icon={<PlusOutlined />} type="primary">
                Add module
              </Button>
            </Dropdown>
          )}
        </Col>
      </Row>

      <Collapse accordion className={styles.collapse} defaultActiveKey={modules.length ? '0' : undefined} expandIconPosition='right'>
        {modules.map((mod, index) => (
          <Panel
            key={String(index)}
            extra={(
              <div onClick={e => e.stopPropagation()} style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
                <Tooltip title="Move up">
                  <Button
                    disabled={index === 0}
                    icon={<ArrowUpOutlined />}
                    onClick={e => { e.stopPropagation(); onReorderModule(index, 'up') }}
                    size="small"
                  />
                </Tooltip>
                <Tooltip title="Move down">
                  <Button
                    disabled={index === modules.length - 1}
                    icon={<ArrowDownOutlined />}
                    onClick={e => { e.stopPropagation(); onReorderModule(index, 'down') }}
                    size="small"
                  />
                </Tooltip>
                <Tooltip title="Delete module">
                  <Button
                    danger
                    icon={<DeleteFilled />}
                    onClick={e => {
                      e.stopPropagation()
                      confirmDeleteModule(index)
                    }}
                    size="small"
                  />
                </Tooltip>
              </div>
            )}
            header={`${mod.type?.charAt(0).toUpperCase()}${mod.type?.slice(1) || ''}`}
            style={{ fontSize: 18 }}
          >
            <Card style={{ borderColor: '#3642b1' }}>
              {mod.type === 'hero' && (
                <UniversalTemplateHeroFields
                  hero={mod.hero}
                  onChange={hero => onUpdateModule(index, { type: mod.type, hero })}
                />
              )}
              {mod.type === 'text' && (
                <UniversalTemplateTextFields
                  onChange={text => onUpdateModule(index, { type: mod.type, text })}
                  text={mod.text}
                />
              )}
              {mod.type === 'form' && (
                <UniversalTemplateFormFields
                  form={mod.form}
                  onChange={form => onUpdateModule(index, { type: mod.type, form })}
                />
              )}
            </Card>
          </Panel>
        ))}
      </Collapse>
    </div>
  )
}

UniversalTemplateModules.propTypes = {
  allGenres: PropTypes.array,
  genre: PropTypes.string,
  modules: PropTypes.array,
  onAddModule: PropTypes.func.isRequired,
  onDeleteModule: PropTypes.func.isRequired,
  onGenreChange: PropTypes.func.isRequired,
  onReorderModule: PropTypes.func.isRequired,
  onUpdateModule: PropTypes.func.isRequired
}

export default UniversalTemplateModules
